import React, { useState, useEffect } from 'react'
import { X, Calendar, Clock, MapPin, RefreshCw } from 'lucide-react'
import { supabase } from '../lib/supabase'

interface Contract {
  id: string
  contract_number: string
  title: string
  contract_type: 'one_time' | 'recurring' | 'as_needed'
  service_type?: string
  is_recurring: boolean
  recurrence_type?: string
  recurrence_days?: number[]
  dsny_integration?: boolean
  dsny_collection_types?: string[]
  interior_cleaning_schedule?: string[]
  start_date?: string
  end_date?: string
  notes?: string
  customer?: {
    id?: string
    company_name?: string
    contact_first_name?: string
    contact_last_name?: string
  }
  property?: {
    id?: string
    name?: string
    address?: string
  }
}

interface UpcomingSchedule {
  date: string
  day: string
  tasks: string[]
  isDSNY: boolean
  isInteriorCleaning: boolean
  jobId?: string
  status?: string
}

interface ContractScheduleModalProps {
  isOpen: boolean
  onClose: () => void
  contract: Contract
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

export function ContractScheduleModal({ isOpen, onClose, contract }: ContractScheduleModalProps) {
  const [upcomingSchedule, setUpcomingSchedule] = useState<UpcomingSchedule[]>([])
  const [loading, setLoading] = useState(false)
  const [daysAhead, setDaysAhead] = useState(30)
  const [isProjection, setIsProjection] = useState(false)

  useEffect(() => {
    if (isOpen && contract) {
      loadSchedule()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, contract, daysAhead])

  const loadSchedule = async () => {
    if (!contract.is_recurring) {
      setUpcomingSchedule([])
      setIsProjection(false)
      return
    }

    setLoading(true)
    try {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const startStr = today.toISOString().split('T')[0]
      const endDate = new Date(today)
      endDate.setDate(today.getDate() + daysAhead)
      const endStr = endDate.toISOString().split('T')[0]

      // 1) Primary source: actual jobs for this contract in the window
      const { data: jobs, error: jobsError } = await supabase
        .from('jobs')
        .select('id, scheduled_date, title, status, dsny_integration, service_type')
        .eq('contract_id', contract.id)
        .gte('scheduled_date', startStr)
        .lte('scheduled_date', endStr)
        .order('scheduled_date', { ascending: true })

      if (!jobsError && jobs && jobs.length > 0) {
        setUpcomingSchedule(
          jobs.map((j: any) => {
            const date = new Date(j.scheduled_date + 'T00:00:00')
            const tasks: string[] = []
            if (j.title) tasks.push(j.title)
            else if (j.service_type) tasks.push(j.service_type)
            else tasks.push('Job')
            return {
              date: j.scheduled_date,
              day: DAY_NAMES[date.getDay()],
              tasks,
              isDSNY: !!j.dsny_integration,
              isInteriorCleaning: false,
              jobId: j.id,
              status: j.status,
            }
          })
        )
        setIsProjection(false)
        return
      }

      // 2) Fallback: project from contract_visit_groups (newer system)
      const { data: groups } = await supabase
        .from('contract_visit_groups')
        .select('id, name, recurrence_type, recurrence_days, start_date, dsny_integration, is_active')
        .eq('contract_id', contract.id)
        .eq('is_active', true)

      const projected: UpcomingSchedule[] = []
      const anchorBase = contract.start_date ? new Date(contract.start_date + 'T00:00:00') : today

      if (groups && groups.length > 0) {
        for (let i = 0; i < daysAhead; i++) {
          const d = new Date(today)
          d.setDate(today.getDate() + i)
          for (const g of groups) {
            const days: number[] = g.recurrence_days || []
            if (days.length === 0) continue
            const dow = d.getDay()
            const anchor = g.start_date ? new Date(g.start_date + 'T00:00:00') : anchorBase
            const matches = matchesRecurrence(d, g.recurrence_type || 'weekly', days, anchor)
            if (matches) {
              projected.push({
                date: d.toISOString().split('T')[0],
                day: DAY_NAMES[dow],
                tasks: [g.name || 'Recurring Visit'],
                isDSNY: !!g.dsny_integration,
                isInteriorCleaning: false,
              })
            }
          }
        }
      } else {
        // 3) Last resort: project from contract-level recurrence_days
        const recurringDays = contract.recurrence_days || []
        for (let i = 0; i < daysAhead; i++) {
          const d = new Date(today)
          d.setDate(today.getDate() + i)
          const dow = d.getDay()
          if (recurringDays.includes(dow)) {
            const tasks: string[] = []
            let isDSNY = false
            if (contract.dsny_integration && contract.dsny_collection_types?.length) {
              isDSNY = true
              tasks.push(...contract.dsny_collection_types.map((t) => `${t} preparation`))
            }
            if (contract.interior_cleaning_schedule?.includes(dow.toString())) {
              tasks.push('Interior cleaning')
            }
            if (tasks.length === 0) tasks.push('Recurring visit')
            projected.push({
              date: d.toISOString().split('T')[0],
              day: DAY_NAMES[dow],
              tasks,
              isDSNY,
              isInteriorCleaning: tasks.includes('Interior cleaning'),
            })
          }
        }
      }

      // De-duplicate projected entries on (date) so same day with multiple groups merges
      const merged: Record<string, UpcomingSchedule> = {}
      for (const p of projected) {
        if (!merged[p.date]) {
          merged[p.date] = { ...p }
        } else {
          merged[p.date].tasks = Array.from(new Set([...merged[p.date].tasks, ...p.tasks]))
          merged[p.date].isDSNY = merged[p.date].isDSNY || p.isDSNY
          merged[p.date].isInteriorCleaning = merged[p.date].isInteriorCleaning || p.isInteriorCleaning
        }
      }

      setUpcomingSchedule(Object.values(merged).sort((a, b) => a.date.localeCompare(b.date)))
      setIsProjection(true)
    } catch (err) {
      console.error('Error loading contract schedule:', err)
      setUpcomingSchedule([])
    } finally {
      setLoading(false)
    }
  }

  // Simple recurrence matcher mirroring the SQL recurrence_matches_date
  const matchesRecurrence = (date: Date, type: string, days: number[], anchor: Date) => {
    const dow = date.getDay()
    if (type === 'daily') return true
    if (type === 'weekly') return days.includes(dow)
    if (type === 'bi_weekly') {
      if (!days.includes(dow)) return false
      const diffDays = Math.floor((date.getTime() - anchor.getTime()) / (1000 * 60 * 60 * 24))
      const weekDiff = Math.floor(diffDays / 7)
      return weekDiff % 2 === 0
    }
    if (type === 'monthly') return date.getDate() === anchor.getDate()
    if (type === 'custom_interval') return days.includes(dow)
    return false
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const getTaskColor = (task: string) => {
    if (
      task.includes('garbage') ||
      task.includes('recycling') ||
      task.includes('organics') ||
      task.includes('bulk')
    ) {
      return 'bg-blue-100 text-blue-800'
    }
    if (task.includes('Interior cleaning')) {
      return 'bg-green-100 text-green-800'
    }
    return 'bg-gray-100 text-gray-800'
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Contract Schedule</h2>
            <p className="text-sm text-gray-600">
              {contract.contract_number} - {contract.title}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Contract Info */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {contract.customer?.company_name ||
                      `${contract.customer?.contact_first_name || ''} ${
                        contract.customer?.contact_last_name || ''
                      }`.trim() ||
                      'Unknown Customer'}
                  </p>
                  <p className="text-xs text-gray-500">Customer</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {contract.property?.name || 'Unknown Property'}
                  </p>
                  <p className="text-xs text-gray-500">Property</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {contract.recurrence_type || 'Custom'} Schedule
                  </p>
                  <p className="text-xs text-gray-500">Frequency</p>
                </div>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700">Show next:</label>
              <select
                value={daysAhead}
                onChange={(e) => setDaysAhead(parseInt(e.target.value))}
                className="px-3 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
                <option value={60}>60 days</option>
                <option value={90}>90 days</option>
              </select>
            </div>
            <div className="text-sm text-gray-500">{upcomingSchedule.length} {isProjection ? 'projected' : 'scheduled'} visits</div>
          </div>

          {/* Projection notice */}
          {isProjection && upcomingSchedule.length > 0 && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-xs text-yellow-800">
              No actual jobs are in the database for this window yet. The list below is a <strong>projection</strong> based on the contract's recurrence settings. Jobs will be created automatically by the nightly process (or by clicking "Run Recurring Top-up Now").
            </div>
          )}

          {/* Schedule List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : upcomingSchedule.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No scheduled visits</h3>
              <p className="text-gray-500">
                {contract.is_recurring
                  ? 'This contract has no recurring schedule configured.'
                  : 'This is a one-time contract with no recurring schedule.'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {upcomingSchedule.map((schedule, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">
                          {formatDate(schedule.date)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">{schedule.day}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {schedule.status && (
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          schedule.status === 'completed' ? 'bg-green-100 text-green-800' :
                          schedule.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                          schedule.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                          schedule.status === 'pending_review' ? 'bg-purple-100 text-purple-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {schedule.status.replace('_', ' ')}
                        </span>
                      )}
                      {schedule.isDSNY && (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          DSNY
                        </span>
                      )}
                      {schedule.isInteriorCleaning && (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          Interior
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {schedule.tasks.map((task, taskIndex) => (
                      <span
                        key={taskIndex}
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTaskColor(
                          task
                        )}`}
                      >
                        {task}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Summary */}
          {upcomingSchedule.length > 0 && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="text-sm font-medium text-blue-900 mb-2">Schedule Summary</h3>
              <div className="text-sm text-blue-800">
                <p>
                  This contract has {upcomingSchedule.length} scheduled visits over the next{' '}
                  {daysAhead} days.
                </p>
                {contract.dsny_integration && (
                  <p className="mt-1">
                    DSNY integration is active with {contract.dsny_collection_types?.length || 0}{' '}
                    collection types.
                  </p>
                )}
                {contract.interior_cleaning_schedule &&
                  contract.interior_cleaning_schedule.length > 0 && (
                    <p className="mt-1">
                      Interior cleaning is scheduled for{' '}
                      {contract.interior_cleaning_schedule.length} days per week.
                    </p>
                  )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
